import React from "react";
import { Box, Container, Typography, Divider } from "@mui/material";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 6 }}>
    <Typography
      variant="h5"
      fontWeight={700}
      sx={{ mb: 2 }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        color: "text.secondary",
        lineHeight: 1.9,
        fontSize: "1rem",
      }}
    >
      {children}
    </Box>
  </Box>
);

const Privacy = () => {
  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: "#f8fafc",
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box mb={8}>
          <Typography
            variant="h3"
            fontWeight={800}
            color = "primary"
            gutterBottom
          >
            Privacy Policy
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.9 }}
          >
            MySchoolling™ (“Company”, “we”, “our”, “us”) provides a SaaS-based
            web and mobile platform designed to help schools manage
            administration, communication, learning workflows, and
            parent engagement.
            <br /><br />
            We are committed to protecting personal data in compliance with
            applicable Indian laws including the Information Technology Act,
            2000, the SPDI Rules, 2011, and the Digital Personal Data Protection
            Act, 2023 (“DPDP Act”).
          </Typography>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Sections */}

        <Section title="1. Information We Collect">
          <ul>
            <li>Personal Information: Name, email, phone number, address, role.</li>
            <li>Sensitive Data: Passwords, payment information (if applicable).</li>
            <li>Academic Data: Attendance, grades, reports, performance data.</li>
            <li>Usage Data: IP address, browser type, device details.</li>
            <li>Communication Data: Messages and notifications within the platform.</li>
          </ul>
        </Section>

        <Section title="2. How We Use Information">
          <ul>
            <li>Provide and improve platform services.</li>
            <li>Manage authentication and user access control.</li>
            <li>Facilitate school-parent communication.</li>
            <li>Process subscriptions and payments.</li>
            <li>Enhance security and prevent fraud.</li>
            <li>Generate anonymized analytics insights.</li>
          </ul>
        </Section>

        <Section title="3. Consent">
          <Typography>
            By using MySchoolling™, you consent to the processing of your
            information in accordance with this policy.
          </Typography>
          <ul>
            <li>
              For children’s data, schools and guardians must ensure lawful
              consent as required under the DPDP Act.
            </li>
            <li>
              Consent may be withdrawn by contacting us, though service access
              may be affected.
            </li>
          </ul>
        </Section>

        <Section title="4. Data Sharing & Disclosure">
          <ul>
            <li>Trusted service providers (cloud, SMS, payment gateways).</li>
            <li>Regulatory or legal authorities when required by law.</li>
            <li>Business restructuring scenarios (merger/acquisition).</li>
            <li>Anonymized, aggregated insights for research and analytics.</li>
          </ul>
          <Typography sx={{ mt: 2 }}>
            We do not sell or rent personal information.
          </Typography>
        </Section>

        <Section title="5. Data Security">
          <ul>
            <li>Encryption in transit and at rest.</li>
            <li>Secure password hashing mechanisms.</li>
            <li>Role-based access control.</li>
            <li>Continuous monitoring and security audits.</li>
          </ul>
          <Typography sx={{ mt: 2 }}>
            While we implement strong safeguards, no system is entirely immune
            from security risks.
          </Typography>
        </Section>

        <Section title="6. Data Retention">
          <ul>
            <li>Data retained only as necessary for service delivery.</li>
            <li>Academic records retained per institutional policy.</li>
            <li>Inactive accounts may be removed after 12 months.</li>
          </ul>
        </Section>

        <Section title="7. Children’s Privacy">
          <Typography>
            As a school platform, we may process children’s data. Schools and
            guardians are responsible for lawful data submission and consent
            compliance.
          </Typography>
        </Section>

        <Section title="8. Your Rights Under DPDP Act">
          <ul>
            <li>Access your personal data.</li>
            <li>Request correction of inaccurate data.</li>
            <li>Request erasure (subject to legal requirements).</li>
            <li>Withdraw consent.</li>
            <li>Nominate a representative.</li>
          </ul>
        </Section>

        <Section title="9. Cookies & Tracking">
          <Typography>
            We use cookies and similar technologies to enhance user experience
            and collect analytics. You may manage cookies in your browser
            settings.
          </Typography>
        </Section>

        <Section title="10. International Transfers">
          <Typography>
            If data is transferred outside India, we ensure appropriate legal
            safeguards in accordance with applicable regulations.
          </Typography>
        </Section>

        <Section title="11. Policy Updates">
          <Typography>
            We may revise this Privacy Policy periodically. Updated versions
            will be posted with an updated Effective Date.
          </Typography>
        </Section>

        <Section title="12. Contact Information">
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            MySchoolling™
          </Typography>
          <Typography>
            Email:{" "}
            <a href="mailto:support@myschoolling.com">
              support@myschoolling.com
            </a>
          </Typography>
          <Typography>
            Phone: +91-9458693639, +91-8700007517
          </Typography>
          <Typography>
            Address: 93/295/21, RDA Colony, Indira Nagar, Raebareli – 229001, UP
          </Typography>
        </Section>

        <Divider sx={{ mt: 8 }} />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 3 }}
        >
          This Privacy Policy is drafted in compliance with the Digital
          Personal Data Protection Act, 2023 and SPDI Rules, 2011 (India).
        </Typography>
      </Container>
    </Box>
  );
};

export default Privacy;